import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";

import DemoBanner from "../components/app/DemoBanner";
import BrowserWarning from "../components/app/BrowserWarning";
import AppHeader from "../components/app/AppHeader";
import PatientSidebar from "../components/app/PatientSidebar";
import PatientPicker from "../components/app/PatientPicker";
import PatientDashboard from "../components/app/PatientDashboard";
import PlanEditor from "../components/app/PlanEditor";
import AllReportsView from "../components/app/AllReportsView";
import NewPlanDialog from "../components/app/NewPlanDialog";
import PlanReadOnlyDialog from "../components/app/PlanReadOnlyDialog";

import {
  SAMPLE_PATIENTS,
  EMPTY_PATIENT,
  EMPTY_EVAL,
  EMPTY_DAILY,
} from "../data/sampleData";

export default function Demo() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState(SAMPLE_PATIENTS);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [view, setView] = useState("empty"); // empty | patient | editing | reports
  const [currentPlan, setCurrentPlan] = useState(null);
  const [newPlanOpen, setNewPlanOpen] = useState(false);
  const [readOnlyPlan, setReadOnlyPlan] = useState(null);
  const [readOnlyOrigin, setReadOnlyOrigin] = useState("patient");

  const selectedPatient = useMemo(
    () => patients.find((p) => p.id === selectedPatientId) || null,
    [patients, selectedPatientId]
  );

  useEffect(() => {
    document.title = "TherapyNote — Demo";
  }, []);

  const selectPatient = (id) => {
    setSelectedPatientId(id);
    setCurrentPlan(null);
    setView("patient");
  };

  // Two flows feed into "create a new plan":
  //  1. Sidebar / empty-state "New plan" button → opens dialog asking for ID + initials.
  //  2. Patient dashboard "New plan" button → uses currently selected patient, no dialog.
  const startBlankPlanFor = (patient) => {
    setCurrentPlan({
      id: `plan-draft-${Date.now()}`,
      date: new Date().toISOString().slice(0, 10),
      type: "evaluation",
      patient: { ...EMPTY_PATIENT },
      evaluation: { ...EMPTY_EVAL },
      daily: { ...EMPTY_DAILY },
      isDraft: true,
    });
    setSelectedPatientId(patient.id);
    setView("editing");
  };

  const handleOpenNewPlanDialog = () => setNewPlanOpen(true);

  const handleNewPlanFromDashboard = () => {
    if (!selectedPatient) {
      toast.error("Select a patient first.");
      return;
    }
    startBlankPlanFor(selectedPatient);
  };

  const handleCreatePlan = ({ id, initials, isExisting }) => {
    let patient;
    if (isExisting) {
      patient = patients.find((p) => p.id === id);
      // If initials differ, keep the existing ones (canonical) but allow update
      if (patient && patient.initials !== initials) {
        const updated = { ...patient, initials };
        setPatients((prev) => prev.map((p) => (p.id === id ? updated : p)));
        patient = updated;
      }
    } else {
      patient = { id, initials, plans: [] };
      setPatients((prev) => [patient, ...prev]);
    }
    setNewPlanOpen(false);
    toast.success(
      isExisting ? `Continuing with ${id}` : `Registered ${id} (demo)`,
      {
        description: isExisting
          ? "Opening a blank plan for today."
          : "Only the ID and initials are stored. Nothing is saved to a database in demo mode.",
      }
    );
    startBlankPlanFor(patient);
  };

  const handleSavePlan = () => {
    toast.success("Saved (demo only)", {
      description:
        "Sign up for a free trial to save this plan to the patient's timeline.",
      action: {
        label: "Start free trial",
        onClick: () => navigate("/signup"),
      },
    });
  };

  const handleBackToPatient = () => {
    setCurrentPlan(null);
    setView("patient");
  };

  const openReadOnly = (plan, origin = "patient") => {
    setReadOnlyOrigin(origin);
    setReadOnlyPlan(plan);
  };

  const goToReports = () => {
    setView("reports");
    setCurrentPlan(null);
  };

  const goToPatientHistory = () => {
    if (!selectedPatient) {
      toast.error("Select a patient first to see their history.");
      return;
    }
    setCurrentPlan(null);
    setView("patient");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0F172A]" data-testid="demo-page">
      <Toaster position="top-center" richColors />
      <DemoBanner />
      <BrowserWarning />
      <AppHeader
        selectedPatient={selectedPatient}
        onPatientHistory={goToPatientHistory}
        onAllReports={goToReports}
        onLogoClick={() => navigate("/")}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-6 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3">
          <PatientSidebar
            patients={patients}
            selectedPatientId={selectedPatientId}
            onSelect={selectPatient}
            onNewPlan={handleOpenNewPlanDialog}
          />
        </aside>

        <main className="lg:col-span-9">
          {view === "empty" && (
            <PatientPicker
              onSelect={selectPatient}
              onNewPlan={handleOpenNewPlanDialog}
              patients={patients}
            />
          )}

          {view === "patient" && selectedPatient && (
            <PatientDashboard
              patient={selectedPatient}
              onNewPlan={handleNewPlanFromDashboard}
              onOpenPlan={(plan) => openReadOnly(plan, "patient")}
            />
          )}

          {view === "editing" && selectedPatient && currentPlan && (
            <PlanEditor
              patient={selectedPatient}
              plan={currentPlan}
              onChange={setCurrentPlan}
              onSave={handleSavePlan}
              onBack={handleBackToPatient}
              forceWhisper={true}
            />
          )}

          {view === "reports" && (
            <AllReportsView
              patients={patients}
              onOpenPlan={(plan, patient) => {
                setSelectedPatientId(patient.id);
                openReadOnly(plan, "reports");
              }}
            />
          )}
        </main>
      </div>

      <NewPlanDialog
        open={newPlanOpen}
        onOpenChange={setNewPlanOpen}
        onCreate={handleCreatePlan}
        existingIds={patients.map((p) => p.id)}
      />

      <PlanReadOnlyDialog
        plan={readOnlyPlan}
        patient={selectedPatient}
        onClose={() => setReadOnlyPlan(null)}
        originLabel={readOnlyOrigin === "reports" ? "All Reports" : "Patient History"}
      />
    </div>
  );
}
